import React, { useCallback, useState } from 'react';

import { useMutation } from '@apollo/client';
import { stringify } from '@stratumn/canonicaljson';
import to from 'await-to-js';
import {
  Code,
  Download,
  History,
  Settings,
  TableColumnsSplit,
  UsersRound
} from 'lucide-react';
import Path from 'path-to-regexp';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { gql } from '@/apollo/gql';
import type { WorkflowQueryQuery } from '@/apollo/graphql';
import { Navbar } from '@/components/layouts';
import { notify } from '@/components/toast';
import { EffectsViewer } from '@/components/ui/effectsViewer/effectsViewer';
import { JsonEditor } from '@/components/ui/utils/jsonEditor/jsonEditor';
import { env } from '@/constant/env';
import {
  // ROUTE_WORKFLOW_BOARD, // Kanban view removed
  ROUTE_WORKFLOW_BATCH_ACTIONS,
  ROUTE_WORKFLOW_EXPORT,
  ROUTE_WORKFLOW_GROUPS,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import {
  TYPE_NEW_WORKFLOW_CONFIG_PAYLOAD,
  TYPE_WORKFLOW,
  TYPE_WORKFLOW_CONFIG
} from '@/gql/types';
import { useUser } from '@/hooks/useUser';

type PartialWorkflowConfig = {
  initState?: unknown;
  initActions?: unknown;
  stateSchema?: unknown;
  actions?: unknown;
  transitions?: unknown;
};

type WorkflowOverviewHeaderProps = {
  workflow?: WorkflowQueryQuery['workflowByRowId'];
  isLoading: boolean;
  // Which view is currently on screen. The navbar entry is a toggle: on the
  // overview it offers "Batch actions"; on the batch history it flips to a
  // highlighted "Workflow overview" so the way back is obvious.
  activeView?: 'overview' | 'batchActions';
};

export const WorkflowOverviewHeader = React.memo(
  (props: WorkflowOverviewHeaderProps) => {
    const { workflow, isLoading, activeView = 'overview' } = props;

    const { isPrivileged } = useUser();
    // replace withRouter with hooks
    // match, history
    const history = useHistory();
    const match = useRouteMatch<{ id: string }>();

    const [updateWorkflowConfigMutation] = useMutation(
      UPDATE_WF_CONFIG_MUTATION
    );

    const [showWorkflowConfigEditor, setShowWorkflowConfigEditor] =
      useState(false);
    const toggleShowWorkflowConfigEditor = useCallback(
      () => setShowWorkflowConfigEditor(!showWorkflowConfigEditor),
      [showWorkflowConfigEditor, setShowWorkflowConfigEditor]
    );

    const [showEffectsViewer, setShowEffectsViewer] = useState(false);
    const toggleShowEffectsViewer = useCallback(
      () => setShowEffectsViewer(!showEffectsViewer),
      [showEffectsViewer]
    );

    const handleWorkflowConfigUpdate = useCallback(
      async (partialConfig: PartialWorkflowConfig) => {
        if (!workflow?.config) return;

        const { rowId: workflowRowId } = workflow;
        const {
          rowId: workflowConfigId,
          overview,
          initState,
          initActions,
          stateSchema,
          actions,
          transitions
        } = workflow.config;

        // Merge current config with partial update
        const newConfig = {
          initState: partialConfig.initState ?? initState,
          initActions: partialConfig.initActions ?? initActions,
          stateSchema: partialConfig.stateSchema ?? stateSchema,
          actions: partialConfig.actions ?? actions,
          transitions: partialConfig.transitions ?? transitions
        };

        const promise = updateWorkflowConfigMutation({
          variables: {
            workflowRowId,
            newInitState: newConfig.initState,
            newInitActions: newConfig.initActions,
            newStateSchema: newConfig.stateSchema,
            newActions: newConfig.actions,
            newTransitions: newConfig.transitions
          },
          optimisticResponse: {
            newWorkflowConfig: {
              workflow: {
                rowId: workflowRowId,
                config: {
                  rowId: workflowConfigId,
                  overview,
                  ...newConfig,
                  __typename: TYPE_WORKFLOW_CONFIG
                },
                __typename: TYPE_WORKFLOW
              },
              __typename: TYPE_NEW_WORKFLOW_CONFIG_PAYLOAD
            }
          }
        });

        notify.promise(promise, {
          loading: '🛠️ Updating the workflow configuration...',
          success: 'The workflow configuration was updated',
          error: data => {
            console.error('Workflow configuration update error: ', data);
            return 'Something went wrong during the workflow configuration update...';
          }
        });

        await to(promise);
      },
      [workflow, updateWorkflowConfigMutation]
    );

    const goToExport = useCallback(
      () =>
        history.push(
          Path.compile(ROUTE_WORKFLOW_EXPORT)({
            id: match.params.id
          })
        ),
      [history, match]
    );

    const goToBatchActions = useCallback(
      () =>
        history.push(
          Path.compile(ROUTE_WORKFLOW_BATCH_ACTIONS)({
            id: match.params.id
          })
        ),
      [history, match]
    );

    const goToWorkflowOverview = useCallback(
      () =>
        history.push(
          Path.compile(ROUTE_WORKFLOW_OVERVIEW)({
            id: match.params.id
          })
        ),
      [history, match]
    );

    const goToGroupsSettings = useCallback(
      () =>
        history.push(
          Path.compile(ROUTE_WORKFLOW_GROUPS)({
            id: match.params.id
          })
        ),
      [history, match]
    );

    let showPrivilegedActions = false;
    let workflowConfigStr: string | undefined;

    if (!isLoading && workflow?.config) {
      showPrivilegedActions = isPrivileged;
      const { initState, initActions, stateSchema, actions, transitions } =
        workflow.config;
      workflowConfigStr = stringify(
        {
          initState,
          initActions,
          stateSchema,
          actions,
          transitions
        },
        null,
        2
      );
    }

    const configHeader = {
      loading: isLoading,
      bottomLevel: {
        workflowPage: true,
        actions: {
          links: [
            ...(showPrivilegedActions
              ? [
                  {
                    icon: <UsersRound />,
                    label: 'Manage groups',
                    onClick: goToGroupsSettings
                  },
                  {
                    icon: <Settings />,
                    label: 'Workflow configuration',
                    onClick: toggleShowWorkflowConfigEditor
                  },
                  {
                    icon: <Code />,
                    label: 'Show effects',
                    onClick: toggleShowEffectsViewer
                  }
                ]
              : []),
            {
              icon: <Download />,
              label: 'Export data',
              onClick: goToExport
            },
            ...(env.FEATURE_FLAGS.USE_BATCH_ACTION
              ? [
                  activeView === 'batchActions'
                    ? {
                        icon: <TableColumnsSplit />,
                        label: 'Workflow overview',
                        onClick: goToWorkflowOverview,
                        className: 'text-primary hover:text-primary'
                      }
                    : {
                        icon: <History />,
                        label: 'Batch actions',
                        onClick: goToBatchActions
                      }
                ]
              : [])
          ]
        }
      }
    };

    if (workflow) {
      // @ts-expect-error I dont know where infoContext is coming from
      configHeader.bottomLevel.infoContext = {
        links: [
          {
            icon: 'TableColumns',
            label: workflow?.name ?? 'Workflow'
          }
        ]
      };
    }

    return (
      <>
        <Navbar config={configHeader} />

        {showPrivilegedActions && showWorkflowConfigEditor && (
          <JsonEditor
            jsonString={workflowConfigStr ?? ''}
            onClose={toggleShowWorkflowConfigEditor}
            onSubmit={async jsonStr => {
              const parsed = jsonStr ? JSON.parse(jsonStr) : {};
              await handleWorkflowConfigUpdate(parsed);
            }}
            title="Workflow configuration"
          />
        )}

        {showPrivilegedActions && showEffectsViewer && (
          <EffectsViewer
            actions={workflow?.config?.actions as Record<string, unknown>}
            onClose={toggleShowEffectsViewer}
            onSubmit={handleWorkflowConfigUpdate}
          />
        )}
      </>
    );
  }
);

const UPDATE_WF_CONFIG_MUTATION = gql(`
  mutation updateWorkflowConfigMutation(
    $workflowRowId: BigInt!
    $newInitState: JSON
    $newInitActions: JSON
    $newStateSchema: JSON
    $newActions: JSON
    $newTransitions: JSON
  ) {
    newWorkflowConfig(
      input: {
        config: {
          workflowRowId: $workflowRowId
          initState: $newInitState
          initActions: $newInitActions
          stateSchema: $newStateSchema
          actions: $newActions
          transitions: $newTransitions
        }
      }
    ) {
      workflow {
        rowId
        config {
          rowId
          overview
          initState
          initActions
          stateSchema
          actions
          transitions
        }
      }
    }
  }
`);
